import { PropsWithChildren } from 'react';

import { useTranslation } from 'react-i18next';

import { Link } from '@cfa/react-components';

import { cn } from '@/utils/tailwind';
import { useRouteMatcher } from '@/utils/url';

import Constants from '@/constants';

import { SortOptions } from '@/types';

import DropdownWithTitle, {
  DropdownWithTitleProps,
} from '../DropdownWithTitle';

export interface SortFilterHeaderProps<
  TLeftDropdownMenuSettings extends { id: string },
> extends PropsWithChildren {
  className?: string;
  label: string;
  onChange: (value: SortOptions | null) => void;
  onClear: () => void;
  options: SortOptions[];
  showClear: boolean;
  showCompletedPlansOption?: boolean;
  showMyCompletedPlansOnlySwitchValue?: boolean;
  text: string;
  title?: string;
  total?: number;
  value: SortOptions | undefined;
  /** A icon button that is placed on the right side of the header. */
  mobileRightSideAction?: React.ReactNode;
  leftDropdownMenuSettings?: DropdownWithTitleProps<TLeftDropdownMenuSettings>;
}

const SortFilterHeader = <TLeftDropdownMenuSettings extends { id: string }>({
  className,
  label,
  leftDropdownMenuSettings,
  onChange,
  onClear,
  options,
  showClear,
  title,
  total,
  value,
  mobileRightSideAction,
}: SortFilterHeaderProps<TLeftDropdownMenuSettings>): React.ReactElement => {
  const { t } = useTranslation();
  const routeMatches = useRouteMatcher();
  const isReportsTeamMembersViewPath = routeMatches.includes(
    Constants.ROUTE_PATH_GROUPS.ReportsTeamMembersViewPath,
  );

  const headerText = (className?: string) => (
    <h3 className={cn('text-variant-h3 truncate', className)}>
      {title} <span className="text-variant-body1 text-heading-3">{total}</span>
    </h3>
  );

  return (
    <div
      className={cn(
        'mb-2 flex w-full items-center justify-between gap-4',
        className,
      )}
      data-testid="SearchFilterHeader"
    >
      {isReportsTeamMembersViewPath &&
        !mobileRightSideAction &&
        headerText('pb-2')}
      {isReportsTeamMembersViewPath && mobileRightSideAction && (
        <div className="flex w-full min-w-0 items-center justify-between gap-4 pb-2">
          {headerText()}
          {mobileRightSideAction}
        </div>
      )}
      <div className="flex min-w-0 items-center justify-end gap-2">
        {!isReportsTeamMembersViewPath && (
          <>
            <h3 className="text-variant-h3 truncate">{title}</h3>
            <h3 className="text-variant-h3 font-normal">{total}</h3>
          </>
        )}
        {showClear && (
          <Link
            onClick={onClear}
            size="body2"
            variant="standalone"
            className="whitespace-nowrap underline"
          >
            {t('Button.clearAll')}
          </Link>
        )}
      </div>
      {!isReportsTeamMembersViewPath && mobileRightSideAction && (
        <div className="flex items-center md:hidden">
          {mobileRightSideAction}
        </div>
      )}

      <div className="hidden flex-row items-center gap-6 md:flex">
        {!!leftDropdownMenuSettings && (
          <DropdownWithTitle {...leftDropdownMenuSettings} />
        )}
        <DropdownWithTitle
          label={label}
          options={options}
          value={value}
          onSelectionChange={onChange}
          displayKey="translationString"
        />
      </div>
    </div>
  );
};

export default SortFilterHeader;
